import { Link } from "react-router-dom";
import { FiArrowRight, FiRepeat, FiUsers, FiHeart, FiLogIn } from "react-icons/fi";
import SectionHeading from "../../components/landing/SectionHeading";
import Reveal from "../../components/landing/Reveal";
import useInView from "../../components/landing/useInView";
import QuranReader from "../../components/landing/illustrations/QuranReader";
import BookReader from "../../components/landing/illustrations/BookReader";
import ReadingCircle from "../../components/landing/illustrations/ReadingCircle";

// Üst bölümdeki sıralı giriş; Home ile aynı dil.
const enter = "animate-hero-in motion-reduce:animate-none";

// Önce âyet ve hadis, büyük ve yan yana; vecize altta, daha küçük ve sakin.
const MAIN_QUOTES = [
  {
    label: "Âyet",
    text: "Allah içinizden inananların ve kendilerine ilim verilenlerin derecelerini yükseltir...",
    source: "Mücâdele, 58/11",
    dark: true,
  },
  {
    label: "Hadis",
    text: "Amellerin Allah Teâlâ'ya en sevimli olanı, az da olsa devamlı yapılanıdır.",
    source: "Müslim, Müsâfirîn, 218",
  },
];

const VECIZE = {
  label: "Vecize",
  text: "Evet, hakikî terakki ise, insana verilen kalb, sır, ruh, akıl… hayat-ı ebediyeye yüzlerini çevirerek… vazife-i ubûdiyetle meşgul olmaktır.",
  source: "Sözler, Yirmi Üçüncü Söz, İkinci Mebhas",
};

const PRINCIPLES = [
  {
    Icon: FiRepeat,
    title: "Devamlılık",
    desc: "Az da olsa her gün. Terakki, büyük hamlelerden çok vazgeçilmeyen küçük adımlarla gelir.",
  },
  {
    Icon: FiUsers,
    title: "Birlikte",
    desc: "Grubunla aynı hedefe yürümek insanı taşır; birbirimizin gayretini görmek şevk verir.",
  },
  {
    Icon: FiHeart,
    title: "Hakiki terakki",
    desc: "Sayılar araçtır; asıl gaye kalbin, ruhun ve aklın ubûdiyetle meşgul olmasıdır.",
  },
];

function Quotes() {
  const [ref, inView] = useInView({ threshold: 0.2 });
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-6 pb-20 md:pb-24 space-y-8 md:space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
        {MAIN_QUOTES.map((quote, i) => (
          <Reveal
            key={quote.source}
            as="figure"
            animation="rise"
            inView={inView}
            delay={i * 0.18}
            className={`relative flex flex-col overflow-hidden rounded-3xl p-8 md:p-11 ${
              quote.dark
                ? "bg-gradient-to-br from-terakkiPrimaryDark via-[#0A5A60] to-terakkiInk text-white shadow-2xl shadow-terakkiInk/25"
                : "border border-white/80 bg-white/75 backdrop-blur shadow-xl shadow-terakkiPrimaryDark/10"
            }`}
          >
            {quote.dark ? (
              <span className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-terakkiPrimary/30 blur-3xl" aria-hidden />
            ) : null}
            <Reveal
              as="span"
              animation="quote"
              inView={inView}
              delay={i * 0.18 + 0.3}
              aria-hidden
              className={`pointer-events-none absolute -top-2 right-6 select-none font-serif text-[9rem] leading-none ${
                quote.dark ? "text-white/10" : "text-terakkiPrimary/15"
              }`}
            >
              ”
            </Reveal>
            <p
              className={`relative text-xs uppercase tracking-[0.22em] ${
                quote.dark ? "text-terakkiBrandSoft" : "text-terakkiPrimaryDark/70"
              }`}
            >
              {quote.label}
            </p>
            <blockquote className="relative mt-5 flex-1 font-serif text-3xl md:text-4xl lg:text-5xl italic leading-snug">
              {quote.text}
            </blockquote>
            <figcaption
              className={`relative mt-8 flex items-center gap-3 text-base ${
                quote.dark ? "text-white/70" : "text-terakkiNeutral"
              }`}
            >
              <span className={`h-px w-10 ${quote.dark ? "bg-white/40" : "bg-terakkiPrimary/50"}`} aria-hidden />
              {quote.source}
            </figcaption>
          </Reveal>
        ))}
      </div>

      <Reveal
        as="figure"
        animation="rise"
        inView={inView}
        delay={0.45}
        className="relative mx-auto max-w-3xl border-l-2 border-terakkiPrimary/40 pl-6 md:pl-8 py-1"
      >
        <p className="text-xs uppercase tracking-[0.22em] text-terakkiPrimaryDark/70">{VECIZE.label}</p>
        <blockquote className="mt-3 font-serif text-lg italic leading-relaxed text-terakkiInk/80">
          {VECIZE.text}
        </blockquote>
        <figcaption className="mt-3 text-sm text-terakkiNeutral">{VECIZE.source}</figcaption>
      </Reveal>
    </section>
  );
}

function Principles() {
  const [ref, inView] = useInView({ threshold: 0.2 });
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-6 pb-20 md:pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] items-center gap-8 lg:gap-14">
        <Reveal animation="in" inView={inView} className="flex justify-center">
          <div className="animate-float-slow motion-reduce:animate-none w-full max-w-[260px] lg:max-w-[380px]">
            <ReadingCircle className="w-full h-auto" />
          </div>
        </Reveal>
        <div className="space-y-6">
          <Reveal inView={inView} delay={0.1}>
            <SectionHeading eyebrow="İlkelerimiz" title="Terakki'yi yaparken gözettiklerimiz" />
          </Reveal>
          <ul className="border-t border-terakkiBrandMist">
            {PRINCIPLES.map(({ Icon, title, desc }, i) => (
              <Reveal
                key={title}
                as="li"
                inView={inView}
                delay={0.25 + i * 0.15}
                className="flex gap-5 border-b border-terakkiBrandMist py-5"
              >
                <Reveal
                  as="span"
                  animation="pop"
                  inView={inView}
                  delay={0.4 + i * 0.15}
                  className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-terakkiBrandMist/80 text-terakkiPrimaryDark"
                >
                  <Icon size={20} aria-hidden />
                </Reveal>
                <div>
                  <h3 className="text-lg font-SemiBold">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-terakkiInk/70">{desc}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function StartBand() {
  const [ref, inView] = useInView({ threshold: 0.2 });
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 md:pb-24">
      <Reveal
        animation="rise"
        inView={inView}
        className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-terakkiPrimaryDark via-[#0A5A60] to-terakkiInk text-white shadow-2xl shadow-terakkiInk/30"
      >
        {/* yumuşak ışık lekeleri */}
        <span className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-terakkiPrimary/40 blur-3xl" aria-hidden />
        <span className="pointer-events-none absolute -bottom-32 right-10 h-80 w-80 rounded-full bg-terakkiBrandSoft/25 blur-3xl" aria-hidden />
        <span className="pointer-events-none absolute right-1/3 top-6 h-40 w-40 rounded-full bg-white/10 blur-3xl animate-hero-shine motion-reduce:animate-none" aria-hidden />

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] items-center gap-8 px-6 py-12 sm:px-10 md:px-14 md:py-16">
          <div className="space-y-6 text-center lg:text-left">
            <Reveal inView={inView} delay={0.2}>
              <h2 className="text-5xl md:text-9xl font-SemiBold leading-tight tracking-tight">
                Terakki yolculuğuna bugün başla
              </h2>
            </Reveal>
            <Reveal inView={inView} delay={0.35}>
              <p className="text-lg text-white/75 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Terakki ile maddi ve manevi gelişimimizi birlikte takip edelim.
              </p>
            </Reveal>
            <Reveal
              inView={inView}
              delay={0.5}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5 pt-2"
            >
              <Link
                to="/mobil-ve-web"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl bg-white px-10 py-5 text-2xl font-SemiBold text-terakkiPrimaryDark shadow-xl shadow-black/20 ring-1 ring-white/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_-8px_rgba(143,201,202,0.75)] focus:outline-none focus-visible:ring-4 focus-visible:ring-terakkiBrandSoft active:translate-y-0"
              >
                Hemen başla
                <FiArrowRight className="text-2xl transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden />
              </Link>
              <Link
                to="/giris"
                className="group inline-flex items-center gap-2 rounded-xl px-3 py-2 text-lg text-white/85 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <FiLogIn aria-hidden />
                <span className="border-b border-white/30 group-hover:border-white/70">Web'den giriş yap</span>
              </Link>
            </Reveal>
          </div>
          <Reveal animation="in" inView={inView} delay={0.45} className="hidden lg:flex justify-center">
            <div className="animate-hero-float motion-reduce:animate-none w-full max-w-[320px]">
              <BookReader className="w-full h-auto drop-shadow-xl" />
            </div>
          </Reveal>
        </div>
      </Reveal>
    </section>
  );
}

/** Misyonumuz: giriş, âyet ve hadis, vecize, ilkeler ve büyük başlama bandı. */
export default function Mission() {
  return (
    <main className="w-full text-terakkiInk">
      <section className="max-w-6xl mx-auto px-6 pt-4 pb-16 md:pb-20 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] items-center gap-8 lg:gap-12">
        <div className="order-2 lg:order-1 space-y-5 text-center lg:text-left">
          <p className={`${enter} text-xs uppercase tracking-[0.22em] text-terakkiPrimaryDark/70`}>Misyonumuz</p>
          <h1
            className={`${enter} text-5xl md:text-8xl font-SemiBold leading-tight tracking-tight`}
            style={{ animationDelay: "0.12s" }}
          >
            Birlik ve manevi{" "}
            <span className="bg-gradient-to-r from-terakkiPrimaryDark via-terakkiPrimary to-terakkiPrimaryDark bg-[length:200%_auto] bg-clip-text text-transparent animate-hero-gradient motion-reduce:animate-none">
              terakki
            </span>{" "}
            yolculuğumuz
          </h1>
          <p
            className={`${enter} text-lg text-terakkiInk/70 leading-relaxed max-w-xl mx-auto lg:mx-0`}
            style={{ animationDelay: "0.26s" }}
          >
            Hâl-i hâzırda günlük olarak yapmakta olduğumuz programları ve hedeflerimizi düzenli takip
            ederek terakkimizi daim kılmak.
          </p>
        </div>
        <div className="order-1 lg:order-2 flex justify-center animate-hero-phone motion-reduce:animate-none" style={{ animationDelay: "0.2s" }}>
          <div className="animate-hero-float motion-reduce:animate-none w-full max-w-[210px] sm:max-w-[260px] lg:max-w-[400px]">
            <QuranReader className="w-full h-auto drop-shadow-sm" />
          </div>
        </div>
      </section>

      <Quotes />
      <Principles />
      <StartBand />
    </main>
  );
}
